import React, { useContext, useState } from "react";
import { ChatContext, EventContext, UserContext, firebase } from "components";
import { useDocumentData } from "react-firebase-hooks/firestore";
import styled from "styled-components";
import { H4, P, Input, Icon, GridRow, Subheader } from "notes";
import { ChatHeader } from "./ChatHeader";
import setLiveChatLogo from "Images/ManagerChatLogo.png";

export const ChatPanel = React.memo(({ team, ...props }) => {
  const { user } = useContext(ChatContext);
  if (!user) {
    return null;
  }
  return (
    <Container isTeam={team} {...props}>
      {team ? (
        <InfoBar xCenter yCenter>
          <H4>Team Chat</H4>
          <Icon indicator name="Information" />
        </InfoBar>
      ) : (
        <ChatHeader />
      )}
      <ShadowOutline isTeam={team}>
        <Shadow />
      </ShadowOutline>
      <ChatBox team={team} />
    </Container>
  );
});

const InfoBar = styled(GridRow)`
  background-color: ${(props) => props.theme.palette.black};
  justify-content: center;
  width: 100%;
  height: 48px;
  ${H4} {
    color: #ffffff;
  }
  svg {
    position: absolute;
    right: 20px;
    path {
      fill: #ffffff;
    }
  }
  @media only screen and ${(props) => props.theme.media.large} {
    display: none;
  }
`;

const ShadowOutline = styled.div`
  display: block;
  width: 100%;
  height: 8px;
  overflow-x: hidden;
  position: absolute;
  top: 56px;
  left: 0;
  @media only screen and ${(props) => props.theme.media.large} {
    ${(props) => props.isTeam && `display: none;`};
  }
`;

const Shadow = styled.div`
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.24);
  display: block;
  position: relative;
  top: -8px;
  width: 100%;
  height: 8px;
`;

const Container = styled.div`
  background-color: ${(props) => (props.isTeam ? "#000000" : "#FAFAFA")};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  ${(props) => !props.isTeam && `flex: 1 1 100%;`};
  @media only screen and ${(props) => props.theme.media.large} {
    ${(props) =>
      !props.isTeam &&
      `border-top: 1px solid ${props.theme.palette.gray.lighter};
      border-left: 1px solid ${props.theme.palette.gray.lighter};
      flex: 0 0 392px;
      margin-top: 40px;`};
  }
`;

export const ChatBox = ({ team, ...props }) => {
  const { chats } = useContext(ChatContext);
  return (
    <BoxContainer {...props}>
      <ChatsContainer>
        {chats?.map((chat) => (
          <ChatMessage key={chat.id} team={team} chat={chat} />
        ))}
      </ChatsContainer>
      <SendMessage team={team} />
    </BoxContainer>
  );
};

const ChatsContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 40px;
  overflow: scroll;
  display: flex;
  flex-direction: column-reverse;
`;

const BoxContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
`;

const ChatMessage = ({ chat, team }) => {
  const { message, sender } = chat.data();
  const { event } = useContext(EventContext);
  const { user } = useContext(UserContext);

  const [displayname, l, e] = useDocumentData(
    firebase.firestore().doc(`meet_greet_names/${sender}`)
  );
  const [roleRecord] = useDocumentData(
    firebase.firestore().doc(`meet_greet/${event.id}/roles/${sender}`)
  );
  const role = roleRecord?.role;
  const name =
    sender === user.id ? (
      "You"
    ) : role !== "manager" || team ? (
      displayname?.value
    ) : (
      <>
        <ManagerLogo src={setLiveChatLogo} />
        Set.Live Team
      </>
    );
  return (
    <ChatContainer>
      <NameContainer>{name}</NameContainer>
      <Message team={team}>{message}</Message>
    </ChatContainer>
  );
};

const ManagerLogo = styled.img`
  flex-shrink: 0;
  margin-right: 6px;
  margin-bottom: -3px;
  width: 16px;
  height: 16px;
`;

const NameContainer = styled(Subheader)`
  margin-bottom: 4px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const ChatContainer = styled.div`
  padding: 0 8px 12px 8px;
`;

const Message = styled(P)`
  color: ${(props) =>
    props.team || props.theme.dark ? "#ffffff" : props.theme.palette.black};
  font-size: 15px;
  line-height: 19px;
`;

const SendMessage = ({ team }) => {
  const { sendMessage } = useContext(ChatContext);
  const [value, setValue] = useState("");

  const handleSend = () => {
    sendMessage(value);
    setValue("");
  };

  return (
    <SendContainer>
      <StyledInput
        value={value}
        onChange={setValue}
        onKeyDown={(e) => {
          e.which === 13 && handleSend();
        }}
        placeholder="Comment..."
        team={team}
        rightIcon={
          <StyledIcon
            onClick={handleSend}
            selector
            name="Send"
            style={{ cursor: "pointer" }}
          />
        }
        style={{ borderRadius: 0 }}
      />
    </SendContainer>
  );
};

const StyledInput = styled(Input)`
  border-radius: 0;
  border: none;
  border-top: 1px solid #e6e9eb;
  ${(props) =>
    props.team &&
    `
    border: 1px solid ${props.theme.palette.gray.lightest}
  `};
`;

const StyledIcon = styled(Icon)`
  width: 33px;
  height: 33px;
  margin-top: -9px;
`;

const SendContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;
